import openai from './openaiClient';
import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error info with isInternal flag and message.
 */
function getErrorMessage(error) {
  // Handle fetch/network errors
  if (error?.message?.includes('Failed to fetch') || error?.name === 'TypeError') {
    return { 
      isInternal: true, 
      message: 'Network error: Unable to reach OpenAI service. This may be due to browser restrictions, network issues, or ad blockers. Please check your connection and try again.' 
    };
  }
  
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes user profile and matches with job listings using GPT-5.
 * @param {Object} userProfile - User's skills, experience, preferences.
 * @param {Array} jobListings - Array of available job listings.
 * @returns {Promise<Object>} Matched jobs with scores and reasons.
 */
export async function analyzeJobMatches(userProfile, jobListings) {
  // Detect browser environment and provide immediate fallback
  // OpenAI API requires backend proxy for production use
  const isBrowserEnvironment = typeof window !== 'undefined';
  
  if (isBrowserEnvironment) {
    console.warn('OpenAI API calls from browser are not recommended. Using client-side matching algorithm.');
    // Return client-side computed results immediately
    return computeClientSideMatches(userProfile, jobListings);
  }

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert job matching AI. Analyze the user profile and job listings to find the best matches. Consider skills, experience, location preferences, and career goals. Provide match scores (0-100) and specific reasons for each match.'
        },
        {
          role: 'user',
          content: `User Profile: ${JSON.stringify(userProfile)}\n\nJob Listings: ${JSON.stringify(jobListings)}\n\nAnalyze and return the top matches with scores and reasons.`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'job_matching_response',
          schema: {
            type: 'object',
            properties: {
              totalJobsAnalyzed: { type: 'number' },
              matchesFound: { type: 'number' },
              averageMatchScore: { type: 'number' },
              processingTime: { type: 'string' },
              matchedJobs: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    jobId: { type: 'number' },
                    matchScore: { type: 'number' },
                    matchReasons: {
                      type: 'array',
                      items: { type: 'string' }
                    },
                    skillsMatched: {
                      type: 'array',
                      items: { type: 'string' }
                    }
                  },
                  required: ['jobId', 'matchScore', 'matchReasons', 'skillsMatched']
                }
              }
            },
            required: ['totalJobsAnalyzed', 'matchesFound', 'averageMatchScore', 'processingTime', 'matchedJobs'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('OpenAI API Error:', error);
    // Fallback to client-side matching
    return computeClientSideMatches(userProfile, jobListings);
  }
}

/**
 * Client-side job matching algorithm as fallback.
 * @param {Object} userProfile - User's skills, experience, preferences.
 * @param {Array} jobListings - Array of available job listings.
 * @returns {Object} Matched jobs with scores and reasons.
 */
function computeClientSideMatches(userProfile, jobListings) {
  const startTime = Date.now();
  
  const matchedJobs = jobListings?.map((job) => {
    let matchScore = 0;
    const matchReasons = [];
    const skillsMatched = [];

    // Skill matching (40% weight)
    const userSkills = userProfile?.skills?.map(s => s?.toLowerCase()) || [];
    const jobSkills = job?.requiredSkills?.map(s => s?.toLowerCase()) || [];
    const matchedSkillsSet = userSkills?.filter(skill => 
      jobSkills?.some(jobSkill => jobSkill?.includes(skill) || skill?.includes(jobSkill))
    );
    
    if (matchedSkillsSet?.length > 0) {
      const skillMatchPercentage = (matchedSkillsSet?.length / jobSkills?.length) * 100;
      matchScore += Math.min(skillMatchPercentage * 0.4, 40);
      skillsMatched?.push(...matchedSkillsSet);
      matchReasons?.push(`${matchedSkillsSet?.length} of ${jobSkills?.length} required skills matched`);
    }

    // Experience matching (25% weight)
    if (userProfile?.experience && job?.experienceRequired) {
      const expYears = parseInt(userProfile?.experience) || 0;
      const reqYears = parseInt(job?.experienceRequired) || 0;
      
      if (expYears >= reqYears) {
        matchScore += 25;
        matchReasons?.push(`Experience requirement met (${expYears}+ years)`);
      } else if (expYears >= reqYears * 0.7) {
        matchScore += 15;
        matchReasons?.push(`Close to experience requirement (${expYears} years)`);
      }
    }

    // Location matching (15% weight)
    if (userProfile?.preferredLocation && job?.location) {
      const userLoc = userProfile?.preferredLocation?.toLowerCase();
      const jobLoc = job?.location?.toLowerCase();
      
      if (jobLoc?.includes(userLoc) || userLoc?.includes(jobLoc) || jobLoc?.includes('remote')) {
        matchScore += 15;
        matchReasons?.push('Location preference matched');
      }
    }

    // Job type matching (10% weight)
    if (userProfile?.preferredJobType && job?.type) {
      const userType = userProfile?.preferredJobType?.toLowerCase();
      const jobType = job?.type?.toLowerCase();
      
      if (jobType?.includes(userType) || userType?.includes(jobType)) {
        matchScore += 10;
        matchReasons?.push('Job type preference matched');
      }
    }

    // Salary expectation (10% weight)
    if (userProfile?.expectedSalary && job?.salary) {
      const expectedSal = parseInt(userProfile?.expectedSalary?.replace(/[^0-9]/g, '')) || 0;
      const jobSal = parseInt(job?.salary?.replace(/[^0-9]/g, '')) || 0;
      
      if (jobSal >= expectedSal * 0.9) {
        matchScore += 10;
        matchReasons?.push('Salary expectation met');
      }
    }

    return {
      jobId: job?.id,
      matchScore: Math.round(matchScore),
      matchReasons: matchReasons?.length > 0 ? matchReasons : ['Basic profile compatibility'],
      skillsMatched: skillsMatched?.length > 0 ? skillsMatched : job?.requiredSkills?.slice(0, 2) || []
    };
  })?.sort((a, b) => b?.matchScore - a?.matchScore);

  const processingTime = `${Date.now() - startTime}ms`;
  const matchesFound = matchedJobs?.filter(j => j?.matchScore >= 50)?.length || 0;
  const averageMatchScore = matchedJobs?.length > 0 
    ? Math.round(matchedJobs?.reduce((sum, j) => sum + j?.matchScore, 0) / matchedJobs?.length)
    : 0;

  return {
    totalJobsAnalyzed: jobListings?.length || 0,
    matchesFound,
    averageMatchScore,
    processingTime,
    matchedJobs
  };
}

/**
 * Parses and screens candidate resumes using GPT-5.
 * @param {Array} candidates - Array of candidate data with resume text.
 * @param {Object} jobRequirements - Job requirements for screening.
 * @returns {Promise<Object>} Ranked candidates with scores.
 */
export async function screenCandidates(candidates, jobRequirements) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert resume screening AI. Analyze candidate profiles against job requirements. Evaluate skills match, experience relevance, education, and projects. Provide detailed scores (0-100) for each category and overall ranking.'
        },
        {
          role: 'user',
          content: `Job Requirements: ${JSON.stringify(jobRequirements)}\n\nCandidates: ${JSON.stringify(candidates)}\n\nScreen and rank these candidates with detailed scoring.`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'candidate_screening_response',
          schema: {
            type: 'object',
            properties: {
              totalScreened: { type: 'number' },
              highlyQualified: { type: 'number' },
              averageScore: { type: 'number' },
              processingTime: { type: 'string' },
              rankedCandidates: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    candidateId: { type: 'number' },
                    overallScore: { type: 'number' },
                    scores: {
                      type: 'object',
                      properties: {
                        skillsMatch: { type: 'number' },
                        experience: { type: 'number' },
                        education: { type: 'number' },
                        projects: { type: 'number' }
                      },
                      required: ['skillsMatch', 'experience', 'education', 'projects']
                    },
                    keyHighlights: {
                      type: 'array',
                      items: { type: 'string' }
                    },
                    status: { type: 'string' }
                  },
                  required: ['candidateId', 'overallScore', 'scores', 'keyHighlights', 'status']
                }
              }
            },
            required: ['totalScreened', 'highlyQualified', 'averageScore', 'processingTime', 'rankedCandidates'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in candidate screening:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes user skills and identifies gaps with course recommendations using GPT-5.
 * @param {Object} userProfile - User's current skills and career goals.
 * @param {Array} marketDemands - Current market skill demands.
 * @returns {Promise<Object>} Skill gap analysis with recommendations.
 */
export async function analyzeSkillGaps(userProfile, marketDemands) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert career development AI. Analyze user skills against market demands to identify skill gaps. Provide importance ratings, demand scores, learning time estimates, and recommend specific skills to learn. Be specific and actionable.'
        },
        {
          role: 'user',
          content: `User Profile: ${JSON.stringify(userProfile)}\n\nMarket Demands: ${JSON.stringify(marketDemands)}\n\nIdentify skill gaps and provide recommendations.`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'skill_gap_analysis_response',
          schema: {
            type: 'object',
            properties: {
              profileCompleteness: { type: 'number' },
              skillsAnalyzed: { type: 'number' },
              gapsIdentified: { type: 'number' },
              recommendedCourses: { type: 'number' },
              currentSkills: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    level: { type: 'number' },
                    status: { type: 'string' }
                  },
                  required: ['name', 'level', 'status']
                }
              },
              skillGaps: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'number' },
                    skill: { type: 'string' },
                    importance: { type: 'string' },
                    demandScore: { type: 'number' },
                    reason: { type: 'string' },
                    impact: { type: 'string' },
                    timeToLearn: { type: 'string' }
                  },
                  required: ['id', 'skill', 'importance', 'demandScore', 'reason', 'impact', 'timeToLearn']
                }
              }
            },
            required: ['profileCompleteness', 'skillsAnalyzed', 'gapsIdentified', 'recommendedCourses', 'currentSkills', 'skillGaps'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'high'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in skill gap analysis:', error);
    }
    throw new Error(errorInfo.message);
  }
}